import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import HikvisionDeviceDashboardSkeleton from '../components/dashboard/HikvisionDeviceDashboardSkeleton'
import HikvisionLiveVideo from '../components/dashboard/HikvisionLiveVideo'
import DeviceAvailabilityAnalytics from '../components/dashboard/DeviceAvailabilityAnalytics'
import DeviceOfflineBanner from '../components/dashboard/DeviceOfflineBanner'
import DevicePageTop from '../components/dashboard/DevicePageTop'
import HikvisionDeviceParams from '../components/dashboard/HikvisionDeviceParams'
import { EditButtons, MetaField } from '../components/dashboard/MetaField'
import { schoolSurface } from '../components/dashboard/schoolEquipmentChrome'
import {
  checkDeviceReachability,
  deleteDoor,
  fetchDoorOverview,
  updateDoor,
} from '../api/hikvision'
import { fetchAlerts } from '../api/dashboard'
import { formatDuration, formatStampAlmaty } from '../lib/formatDuration'
import { pickDoorDownSince } from '../lib/deviceDownSince'
import { kpiDoorStatus, resolveDoorState } from '../lib/doorState'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const matchState = (val, mapping) => {
  if (val === null || val === undefined) return '—'
  const key = String(Number(val))
  return mapping[key] ?? '—'
}


const HikvisionDeviceDashboard = ({ setIsAuthenticated, onLogout, userRole }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ip, doorId } = useParams()

  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const [overview, setOverview] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [seriesRefreshKey, setSeriesRefreshKey] = useState(0)
  const [downAlerts, setDownAlerts] = useState([])

  const [editingField, setEditingField] = useState(null)
  const [draft, setDraft] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  const [checkResult, setCheckResult] = useState(null)

  const canManage = isAdminOrEngineer(userRole)

  const fetchData = async ({ silent = false } = {}) => {
    if (!doorId) return
    if (silent) setRefreshing(true)
    else setIsLoading(true)
    setError(null)
    try {
      const [overviewData, alertsData] = await Promise.all([
        fetchDoorOverview(doorId),
        fetchAlerts().catch(() => []),
      ])
      if (overviewData?.device?.device_type === 'panic') {
        navigate(`/schools/${encodeURIComponent(ip)}/panic/${doorId}`, { replace: true })
        return
      }
      if (overviewData?.device?.device_type === 'nvr') {
        navigate(`/schools/${encodeURIComponent(ip)}/nvr/${doorId}`, { replace: true })
        return
      }
      setOverview(overviewData)
      setDownAlerts(Array.isArray(alertsData) ? alertsData : [])
      setEditingField(null)
      if (silent) setSeriesRefreshKey((key) => key + 1)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [doorId])

  const handleSave = async (body) => {
    setIsSaving(true)
    setSaveError(null)
    try {
      await updateDoor(doorId, body)
      setEditingField(null)
      setDraft({})
      fetchData({ silent: true })
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Ошибка сохранения')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Удалить это устройство? Действие необратимо.')) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteDoor(doorId)
      navigate(`/schools/${encodeURIComponent(ip)}`)
    } catch (err) {
      setDeleteError(err?.response?.data?.message || err?.message || 'Ошибка удаления')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCheckReachability = async () => {
    if (!device?.host) {
      setCheckResult({ reachable: false, message: t('schoolDashboard.hikCheckHostRequired') })
      return
    }
    setIsChecking(true)
    setCheckResult(null)
    setDeleteError(null)
    try {
      const data = await checkDeviceReachability({
        id: Number(doorId),
        host: device.host,
        port: device.port,
        use_https: !!device.use_https,
        device_type: device.device_type || 'door',
      })
      setCheckResult(data)
    } catch (err) {
      setCheckResult({
        reachable: false,
        message: err?.response?.data?.message || err?.message || t('schoolDashboard.hikCheckFailed'),
      })
    } finally {
      setIsChecking(false)
    }
  }

  const startEdit = (field, value) => {
    setSaveError(null)
    setEditingField(field)
    setDraft(value)
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setDraft({})
    setSaveError(null)
  }

  const device = overview?.device
  const metrics = overview?.metrics
  const source = overview?.source
  const doors = metrics?.doors || {}
  const upValue = metrics?.up === null || metrics?.up === undefined ? null : Number(metrics.up)
  const isUp = upValue === 1
  const isDown = upValue === 0
  const address =
    device?.host && device?.port != null ? `${device.host}:${device.port}` : device?.target
  const downSince = pickDoorDownSince(downAlerts, {
    deviceId: doorId,
    target: address || device?.target,
  })

  const chartEmptyMessage = useMemo(() => {
    if (source && source.live === false && source.prometheus === false) {
      return 'Exporter недоступен — невозможно опросить устройство.'
    }
    if (source && source.prometheus === false) {
      return 'Метрики собираются. Исторические данные появятся после нескольких циклов сбора.'
    }
    return 'Нет данных для отображения за выбранный период.'
  }, [source])

  const sortedDoors = useMemo(
    () => Object.entries(doors).sort(([a], [b]) => Number(a) - Number(b)),
    [doors]
  )

  const info = metrics?.device_info || {}
  const model = device?.model || info.model
  const firmware = device?.firmware || info.firmware
  const serial = device?.serial || info.serial
  const downDuration = formatDuration(downSince)
  const downStamp = formatStampAlmaty(downSince)
  const statusKpi = isUp ? kpiDoorStatus(sortedDoors) : null
  const tamperTone =
    Number(metrics?.tamper_alarm) === 1
      ? 'text-critical'
      : Number(metrics?.tamper_alarm) === 0
        ? 'text-positive'
        : undefined
  const stats = [
    { label: t('door.doors'), value: isUp ? sortedDoors.length || null : null },
    {
      label: t('door.tamper'),
      value: isUp && metrics?.tamper_alarm != null ? matchState(metrics.tamper_alarm, { 0: 'ОК', 1: 'Тревога' }) : null,
      tone: tamperTone,
    },
    {
      label: t('door.events'),
      value: isUp && metrics?.acs_events_total != null ? parseInt(metrics.acs_events_total, 10).toLocaleString('ru-KZ') : null,
    },
    {
      label: t('door.status'),
      value: isUp ? (statusKpi?.label ?? t('common.online')) : null,
      tone: isUp ? (statusKpi?.color || 'text-positive') : undefined,
    },
  ]

  const goToSchool = () => navigate(`/schools/${encodeURIComponent(ip)}`)
  const refresh = () => fetchData({ silent: true })

  const analyticsSection = (
    <DeviceAvailabilityAnalytics
      t={t}
      deviceId={doorId}
      currentUp={upValue}
      downSince={downSince}
      isDown={isDown}
      refreshKey={seriesRefreshKey}
      modes={[
        { id: 'availability', label: t('nvr.chartAvailability'), title: t('door.availability') },
        { id: 'events', label: t('door.chartEvents'), title: t('door.eventsOverTime') },
      ]}
      secondaryKind="events"
      eventDoorId={Object.keys(doors)[0] || '1'}
      emptyMessage={chartEmptyMessage}
    />
  )

  const extraFields = (
    <MetaField
      label={t('door.videoChannel')}
      editing={editingField === 'channel'}
      canEdit={canManage}
      onStartEdit={() => startEdit('channel', { video_channel: device.video_channel || '101' })}
      editTitle={t('common.edit')}
      mono
      editor={
        <>
          <select
            value={draft.video_channel ?? '101'}
            onChange={(e) => setDraft((d) => ({ ...d, video_channel: e.target.value }))}
            className="min-w-[140px] rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            disabled={isSaving}
          >
            <option value="101">101 (main)</option>
            <option value="102">102 (sub)</option>
          </select>
          <EditButtons
            onSave={() => handleSave({ video_channel: String(draft.video_channel || '101') })}
            onCancel={handleCancelEdit}
            updateLoading={isSaving}
          />
        </>
      }
    >
      {device?.video_channel || '101'}
    </MetaField>
  )

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="absolute inset-0 -z-10 bg-brand-radial" />
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((prev) => !prev)}
        onLogout={onLogout}
        userEmail={localStorage.getItem('userEmail') || ''}
        userRole={userRole}
        setIsAuthenticated={setIsAuthenticated}
      />
      <div className="flex flex-1">
        {isSidebarVisible && (
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            onLogout={onLogout}
            userRole={userRole}
            onClose={() => setSidebarVisible(false)}
          />
        )}
        <main className="relative flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-4 py-6 transition-all duration-300 ease-brand sm:px-6 lg:px-10">
          <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-brand-linear/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-64 bg-brand-radial blur-3xl" />

          {isLoading ? (
            <HikvisionDeviceDashboardSkeleton />
          ) : error ? (
            <div className="rounded-3xl bg-critical/10 px-6 py-8 text-sm text-critical">{error}</div>
          ) : !device ? (
            <div className="rounded-3xl bg-surface-1 px-6 py-12 text-center text-sm text-muted-300">
              Устройство не найдено
            </div>
          ) : (
            <>
              <DevicePageTop
                crumbLabel={device.school || t('schoolDashboard.backToList')}
                onBack={goToSchool}
                kind={t('door.kind')}
                title={device.name}
                isUp={isUp}
                isDown={isDown}
                downDuration={downDuration}
                address={address}
                model={model}
                firmware={firmware}
                showDetails={showDetails}
                onToggleDetails={() => setShowDetails((v) => !v)}
                onRefresh={refresh}
                refreshing={refreshing}
                t={t}
              />

              {showDetails && (
                <HikvisionDeviceParams
                  device={device}
                  metrics={metrics}
                  serial={serial}
                  canManage={canManage}
                  t={t}
                  editingField={editingField}
                  draft={draft}
                  setDraft={setDraft}
                  startEdit={startEdit}
                  onCancelEdit={handleCancelEdit}
                  onSave={handleSave}
                  isSaving={isSaving}
                  saveError={saveError}
                  extraFields={extraFields}
                  onCheckReachability={handleCheckReachability}
                  isChecking={isChecking}
                  checkResult={checkResult}
                  onDelete={handleDelete}
                  isDeleting={isDeleting}
                  deleteError={deleteError}
                />
              )}

              {isDown && (
                <DeviceOfflineBanner
                  downStamp={downStamp}
                  noConnectionKey="door.noConnection"
                  noConnectionSinceKey="door.noConnectionSince"
                  hintKey="door.offlineHint"
                  onRetry={refresh}
                  t={t}
                />
              )}

              <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-2xl bg-white/[0.03] px-4 py-3">
                    <p className="text-xs text-muted-500">{s.label}</p>
                    <p
                      className={`mt-1 font-display text-2xl tabular-nums ${
                        s.value == null ? 'text-muted-600' : s.tone || 'text-white'
                      }`}
                    >
                      {s.value ?? '—'}
                    </p>
                  </div>
                ))}
              </section>

              {!isUp && analyticsSection}

              {isUp ? (
                <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                  <div className="xl:col-span-4">
                    <div className={`w-full rounded-2xl p-3 ${schoolSurface.section}`}>
                      <header className="mb-2">
                        <h2 className="font-display text-base text-white">
                          {t('door.doors')}{' '}
                          <span className="text-muted-500">{sortedDoors.length || '—'}</span>
                        </h2>
                      </header>
                      {sortedDoors.length === 0 ? (
                        <div className="flex h-32 items-center justify-center rounded-xl bg-surface-2 text-sm text-muted-500">
                          {t('door.noDoors')}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {sortedDoors.map(([doorKey, door]) => {
                            const status = resolveDoorState(door)
                            const lockOpen = Number(door.lock) === 1
                            const magnetOpen = Number(door.magnet) === 1
                            return (
                              <div key={doorKey} className={`rounded-lg px-2.5 py-1.5 ${schoolSurface.card}`}>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <div className="min-w-[2.75rem]">
                                    <p className="text-[10px] text-muted-500">{t('door.door')}</p>
                                    <p className="font-display text-sm text-white">№ {doorKey}</p>
                                  </div>
                                  <span className={`text-[11px] font-medium ${status?.color || 'text-positive'}`}>
                                    {status?.label || t('common.online')}
                                  </span>
                                  <div className="ml-auto flex flex-wrap gap-1 text-[11px]">
                                    <div className={`rounded-md px-1.5 py-0.5 ${schoolSurface.tile}`}>
                                      <span className="text-muted-500">{t('door.lock')} </span>
                                      <span className={lockOpen ? 'text-warning' : 'text-positive'}>
                                        {matchState(door.lock, { 0: 'Закрыт', 1: 'Открыт' })}
                                      </span>
                                    </div>
                                    <div className={`rounded-md px-1.5 py-0.5 ${schoolSurface.tile}`}>
                                      <span className="text-muted-500">{t('door.magnet')} </span>
                                      <span className={magnetOpen ? 'text-critical' : 'text-positive'}>
                                        {matchState(door.magnet, { 0: 'Закрыт', 1: 'Открыт' })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {door.last_event_ts && (
                                  <p className="mt-1 text-[10px] tabular-nums text-muted-400">
                                    {formatStampAlmaty(door.last_event_ts) || '—'}
                                  </p>
                                )}
                                {door.face_failures_5m != null && Number(door.face_failures_5m) > 0 && (
                                  <p className="mt-0.5 text-[10px] text-warning">
                                    {t('door.faceFailures', { count: door.face_failures_5m })}
                                  </p>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="xl:col-span-8">
                    <div className={`rounded-3xl p-6 ${schoolSurface.section}`}>
                      <header className="mb-4">
                        <h2 className="font-display text-lg text-white">{t('door.video')}</h2>
                      </header>
                      <div className="aspect-video overflow-hidden rounded-2xl bg-surface-2">
                        {device.video_enabled !== false ? (
                          <HikvisionLiveVideo
                            key={`${device.id}-${device.video_channel || '101'}`}
                            deviceId={device.id}
                            videoEnabled
                            defaultChannel={device.video_channel || '101'}
                            className="h-full"
                          />
                        ) : (
                          <div className="flex h-full min-h-[16rem] items-center justify-center text-sm text-muted-500">
                            {t('door.videoDisabled')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="flex items-center gap-3 rounded-3xl bg-surface-1 px-6 py-5 text-sm text-muted-400">
                  <span className="material-icons text-muted-600">videocam_off</span>
                  {t('door.doorsUnavailable')}
                </section>
              )}

              {isUp && analyticsSection}
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default HikvisionDeviceDashboard
